import { push } from 'redux-first-history';
import { all, takeEvery, put, call } from 'redux-saga/effects';
import moment from 'moment';
import actions from './actions';
import Api, { executeAction } from '../../../helpers/api';
import { sendRequest } from '../../../helpers/saga';
import { logger } from '../../../helpers/logger';
import logsActions from './logs/actions';
import notifications from '../../../redux/notifications/actions';
import { keyByProperty } from '../../../helpers/core';
import { getLastVisitedRoute } from '../../../helpers/utility';
import { t } from '../../../i18n';

const api = new Api('workers/:id');
const apiForAvailabilities = new Api('workers/:id/availabilities');
const resendContractsEndpoint = new Api('workers/:id/contracts/resend');

function convertSkills(skills) {
  if (!skills) {
    return [];
  }

  return skills.map(skill => {
    if (!skill.position) {
      return skill;
    }
    const { position, ...rest } = skill;

    return { position_id: position.id, position_name: position.name, ...rest };
  });
}

function* loadData(payloadData) {
  try {
    const { id, isEdit } = payloadData.payload;

    const { result, formInfo } = yield all({
      result: yield call(sendRequest, api.load, { id }, notifications.REQUEST_ERROR, null, true),
      formInfo: isEdit ? call(sendRequest, api.loadFormData, { id }, notifications.REQUEST_ERROR) : null,
    });

    if (result) {
      const { data } = result;

      yield put(actions.loadDataSuccess({ data: { ...data } }));

      if (isEdit) {
        yield put(actions.loadFormData(formInfo));
        data.skills = convertSkills(data.skills);
        yield put(actions.edit({ id, talent: { ...data, address: '' } }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadAvailabilitiesData(payloadData) {
  try {
    const {
      payload: { date, id },
    } = payloadData;
    const result = yield call(
      sendRequest,
      apiForAvailabilities.load,
      { id, search: { date } },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      const { data } = result;

      yield put(actions.loadAvailabilitiesDataSuccess({ data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* updateTalent(data) {
  try {
    const { payload } = data;
    const { editForm, id, hidden_fields } = payload;

    const propsArray = Object.values(editForm);
    const talent = keyByProperty(propsArray, 'name', prop => prop.value);

    const competencies_attributes = talent.skills.map(item => {
      return {
        ...item,
        level: item.level.id,
        position_id: item.position_id,
        id: item.newSkill ? null : item.id,
      };
    });

    talent.birthday = talent.birthday ? talent.birthday.slice(0, 10) : null;
    hidden_fields.forEach(item => delete talent[item]);
    talent.country_id = talent.country;

    if (talent.hired_date) {
      talent.hired_date = moment(talent.hired_date).toISOString().replace('.000Z', 'Z');
    }

    talent.tax_information_attributes =
      talent.tax_information && talent.tax_information.tax_file_number
        ? {
            id: talent.tax_information.id,
            tax_file_number: talent.tax_information && talent.tax_information.tax_file_number.replace(/ /g, ''),
          }
        : null;
    delete talent.tax_information;

    const result = yield call(
      sendRequest,
      api.updateItem,
      {
        data: {
          worker: {
            ...talent,
            competencies_attributes,
          },
        },
        id,
      },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      if (result.data) {
        const lastRoute = getLastVisitedRoute();
        const redirectUrl = lastRoute.substr(0, lastRoute.lastIndexOf('/'));
        const { skills } = talent;
        const outputSkills = skills
          .map(item => {
            // eslint-disable-next-line no-underscore-dangle
            if (item._destroy) {
              return null;
            }

            return item;
          })
          .filter(item => {
            return item !== null;
          });

        yield put(
          actions.saveSuccess({
            data: {
              ...talent,
              skills: outputSkills,
            },
          }),
        );

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.updated_successfully', { item: t('dash.talent.worker_title') }),
          }),
        );
        yield put(push(redirectUrl));
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.error_headline'),
            type: 'error',
            message: t('dash.common.notification.check_validation_errors'),
          }),
        );

        yield put(actions.saveError({ data: result }));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* deleteTalent({ payload }) {
  try {
    const { id } = payload;

    const result = yield call(sendRequest, api.deleteItem, { id }, notifications.REQUEST_ERROR);

    if (result) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.removed_successfully', { item: t('dash.talent.worker_title') }),
        }),
      );
      yield put(actions.deleteSuccess());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormData({ payload }) {
  try {
    if (!payload.id) {
      return;
    }
    const result = yield call(sendRequest, api.loadFormData, { id: payload.id }, notifications.REQUEST_ERROR);

    if (result) {
      const { data } = result;

      yield put(actions.loadFormData({ data }));
    }
  } catch (error) {
    logger.error(error);
  }
}

function* sendShiftRequest({ payload }) {
  try {
    const { url, method } = payload;

    const result = yield call(sendRequest, executeAction, { url, method }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: `${t('dash.common.notification.success_headline')}.`,
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* changeStatus({ payload }) {
  try {
    const { method, url, id, data } = payload;

    const result = yield call(sendRequest, executeAction, { url, method, data }, notifications.REQUEST_ERROR);

    if (result && result.data) {
      // Update Talent view page
      yield put(actions.loadData({ id }));
      // Update Logs section
      yield put(logsActions.loadData({ id }));
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.has_been_changed', { item: t('dash.talent.status_label') }),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* resendContracts({ payload }) {
  try {
    const result = yield call(
      sendRequest,
      resendContractsEndpoint.updateItem,
      { id: payload.id },
      notifications.REQUEST_ERROR,
    );

    if (result) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.talent.re_sent_contracts_notification'),
        }),
      );
      yield put(actions.resendContractsSuccess());
    } else {
      yield put(actions.resendContractsError());
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.resendContractsError());
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.LOAD, loadData),
    takeEvery(actions.LOAD_AVAILABILITIES, loadAvailabilitiesData),
    takeEvery(actions.SAVE, updateTalent),
    takeEvery(actions.DELETE, deleteTalent),
    takeEvery(actions.TOGGLE_TERMINATION_MODAL, loadFormData),
    takeEvery(actions.SEND_REQUEST, sendShiftRequest),
    takeEvery(actions.CHANGE_STATUS, changeStatus),
    takeEvery(actions.RESEND_CONTRACTS_REQUEST, resendContracts),
  ]);
}
